import { useCallback, useRef, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import confetti from 'canvas-confetti';

/**
 * Hook for detecting "metefy" easter egg in text inputs
 * Optimized to only check when value increases (user typing forward)
 */
export function useMetefyEasterEgg() {
  const { toast } = useToast();
  const lastFieldValues = useRef<Map<string, string>>(new Map());
  const easterEggAudioRef = useRef<HTMLAudioElement | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize audio on mount
  useEffect(() => {
    easterEggAudioRef.current = new Audio('https://res.cloudinary.com/deqzsmrxq/video/upload/v1766776475/faaah_ab9gz0.mp3');
    easterEggAudioRef.current.volume = 0.5;
    
    return () => {
      if (easterEggAudioRef.current) {
        easterEggAudioRef.current.pause();
        easterEggAudioRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const triggerMetefyEasterEgg = useCallback((fieldName: string, currentValue: string) => {
    // Debounce the check to avoid running on every keystroke
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      const lastValue = lastFieldValues.current.get(fieldName) || '';
      
      // Early exit if value hasn't changed or decreased
      if (currentValue.length <= lastValue.length) {
        lastFieldValues.current.set(fieldName, currentValue);
        return;
      }
      
      // Only check if "metefy" might be newly added
      const newPart = currentValue.slice(lastValue.length).toLowerCase();
      const lastHadMetefy = lastValue.toLowerCase().includes('metefy');
      
      // Quick check: if new part doesn't contain relevant chars, skip
      if (!newPart.match(/[metfy]/i)) {
        lastFieldValues.current.set(fieldName, currentValue);
        return;
      }
      
      const currentHasMetefy = currentValue.toLowerCase().includes('metefy');
      
      // Only trigger when "metefy" appears (not when it was already there)
      if (currentHasMetefy && !lastHadMetefy) {
        // Play sound effect
        if (easterEggAudioRef.current) {
          easterEggAudioRef.current.currentTime = 0;
          easterEggAudioRef.current.play().catch((error) => {
            console.log('Audio play failed:', error);
          });
        }
        
        // Trigger confetti celebration
        const duration = 3000;
        const animationEnd = Date.now() + duration;
        const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

        function randomInRange(min: number, max: number) {
          return Math.random() * (max - min) + min;
        }

        const interval: any = setInterval(function() {
          const timeLeft = animationEnd - Date.now();

          if (timeLeft <= 0) {
            return clearInterval(interval);
          }

          const particleCount = 50 * (timeLeft / duration);
          
          confetti({
            ...defaults,
            particleCount,
            origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
          });
          confetti({
            ...defaults,
            particleCount,
            origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
          });
        }, 250);

        // Show toast message
        toast({
          title: "🎉 Easter Egg Found!",
          description: "You discovered the secret word 'metefy'!",
          duration: 3000,
        });
      }
      
      // Update the last value for this field
      lastFieldValues.current.set(fieldName, currentValue);
    }, 300); // 300ms debounce
  }, [toast]);

  return { triggerMetefyEasterEgg };
}


